/**
 * SyncedRadioPlayer — tunes in to the server-side canonical radio stream.
 *
 * Every listener who opens this component at the same moment hears the
 * same track at the same position. The player:
 *   • polls /api/radio/now-playing every 30 seconds to re-sync
 *   • seeks to server-indicated `seek_seconds` on first play
 *   • auto-advances to the next track when remaining hits 0
 *   • shows a gold "🎙️ Requested by @user · VIP Member" banner when
 *     a VIP request is inserted into the queue (during 6-10pm UTC)
 *
 * Station = 'ai' or 'music'. Optional `personalize` mode pulls from
 * /api/radio/my-radio which overlays the user's purchased tracks.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Radio, Play, Pause, Volume2, VolumeX, Sparkles, Users, Share2, Link2, ShoppingBag, ArrowRight, Maximize2, SkipBack, SkipForward, Rewind, FastForward, ListMusic, Disc } from 'lucide-react';
import { Button } from './ui/button';
import AnimatedWaveform from './AnimatedWaveform';
import { useAudioAnalyser } from '../hooks/useAudioAnalyser';
import { MarqueeText } from './MarqueeText';
import { RadioFullscreenOverlay } from './RadioFullscreenOverlay';
import { useNonVipSkipQuota, formatCooldown, SKIP_QUOTA } from './radio/useNonVipSkipQuota';
import { RadioBannerAd, requestNextAd, recordEvent as recordAdEvent } from './radio/RadioAds';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 30_000;

const STATION_TITLES = {
  all: "One Voice All Genre Radio",
  ai: "AI Radio",
  music: "Music Radio",
  beats: "Beats Radio",
};

// Per-station visual accent for the waveform + UI highlights.
const STATION_ACCENT = {
  all: 'gold',
  ai: 'pink',
  music: 'cyan',
  beats: 'green',
};

function fmt(sec) {
  if (!sec && sec !== 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SyncedRadioPlayer({
  station = 'ai',
  token,
  personalize = false,
  // "locked" mode: user can only mute/volume — no pause/seek (parent admin
  // can override with `adminOverride`). Used for the All-Genre 24/7 feed.
  locked = false,
  adminOverride = false,
  // Enable "Share" button (copies the OG preview URL for the station).
  shareable = false,
  // Try to autoplay on mount. Browsers block audio autoplay unless muted,
  // so we start muted and prompt the user to tap-to-unmute.
  autoplay = false,
  // Optional list of stations + callback to switch from inside the
  // fullscreen overlay (CarPlay-style "Now Playing" picker).
  stations = null,
  onStationChange = null,
  // Controlled fullscreen state (so the overlay can survive a parent
  // remount when the user switches stations from inside it).
  fullscreen: fullscreenProp,
  onFullscreenChange,
}) {
  const [state, setState] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  // When autoplay is requested we start muted so the browser allows it.
  // The visible "tap to unmute" banner converts the first gesture to sound.
  const [muted, setMuted] = useState(!!autoplay);
  const [pos, setPos] = useState(0); // current time within the track
  const [activeSlate, setActiveSlate] = useState(null);
  const [awaitingUnmute, setAwaitingUnmute] = useState(!!autoplay);
  const [fullscreenInternal, setFullscreenInternal] = useState(false);
  const fullscreen = fullscreenProp !== undefined ? fullscreenProp : fullscreenInternal;
  const setFullscreen = (v) => {
    if (onFullscreenChange) onFullscreenChange(typeof v === 'function' ? v(fullscreen) : v);
    else setFullscreenInternal(v);
  };

  // VIP / admin gating for advanced controls (skip, FF, RW, queue).
  // The All-Genre stream stays locked for everyone (synced 24/7 broadcast),
  // so even VIPs only get controls on Music / AI / Beats stations.
  const { user } = useAuth();
  const isVip = !!(user?.is_vip || user?.is_admin);
  const advancedControlsEnabled = !locked || adminOverride;
  // Non-VIP skip quota: 3 skips per 15-min rolling window on the
  // unlocked stations (Music / AI / Beats). Skips are local seeks
  // forward in the schedule — they do NOT affect other listeners.
  const skipQuota = useNonVipSkipQuota(station);
  const [showQueue, setShowQueue] = useState(false);
  const queuePanelRef = useRef(null);

  // When the queue panel becomes visible, smooth-scroll it into view so
  // mobile users (where the player itself is tall) can immediately see it.
  useEffect(() => {
    if (showQueue && queuePanelRef.current) {
      const t = setTimeout(() => {
        try { queuePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        catch { /* iOS Safari quirk */ }
      }, 80);
      return () => clearTimeout(t);
    }
  }, [showQueue]);
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  // The track whose metadata is being broadcast to CarPlay / Android Auto /
  // lock-screen via the Media Session API. Normally this mirrors
  // state.current_track, but when a standalone track plays (VIP request
  // or personal-library overlay) we switch to that track so the car
  // screen always shows what the listener is actually hearing.
  const [displayTrack, setDisplayTrack] = useState(null);
  const audioRef = useRef(null);
  const lastTrackIdRef = useRef(null);
  const personalQueueRef = useRef([]);
  // Web Audio AnalyserNode wiring — `analyser.levels` is a live FFT snapshot
  // (Uint8Array, length 32, bass-weighted) used by AnimatedWaveform for
  // bar-by-bar reactivity. Falls back gracefully on CORS failures.
  const analyser = useAudioAnalyser(audioRef, { bars: 32 });
  const slotCounterRef = useRef(0);
  const autoplayTriedRef = useRef(false);
  // Per-track play-time accumulator. Resets on each track change. Once it
  // crosses 60s we fire /api/stream/record once (flag in recordedRef).
  const playedSecondsRef = useRef(0);
  const recordedRef = useRef(false);

  const fetchNow = useCallback(async () => {
    try {
      const url = personalize && token
        ? `${API}/radio/my-radio?station=${station}`
        : `${API}/radio/now-playing?station=${station}`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get(url, { headers });
      setState(r.data);
      return r.data;
    } catch (e) {
      console.error('[radio] fetch failed:', e);
      return null;
    }
  }, [station, personalize, token]);

  // Initial + periodic sync
  useEffect(() => {
    fetchNow();
    const id = setInterval(fetchNow, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchNow]);

  // When current_track changes, re-point audio src and seek
  useEffect(() => {
    const s = state;
    if (!s || !s.current_track) return;
    const t = s.current_track;
    if (t.id === lastTrackIdRef.current) return;
    lastTrackIdRef.current = t.id;
    // Reset per-track play accumulator (new track → new potential play count)
    playedSecondsRef.current = 0;
    recordedRef.current = false;

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
      audioRef.current.preload = 'auto';
      audioRef.current.crossOrigin = 'anonymous';
    }
    const streamUrl = t.filename
      ? `${BACKEND_URL}/api/stream/track/${t.filename}`
      : null;
    if (!streamUrl) return;
    audioRef.current.src = streamUrl;
    audioRef.current.currentTime = Math.max(0, s.seek_seconds || 0);
    setPos(s.seek_seconds || 0);
    // Mirror to Media Session metadata (unless a standalone/VIP track is
    // currently broadcasting). Keeps CarPlay in sync with the schedule.
    setDisplayTrack(t);
    if (playing) {
      audioRef.current.play().catch(() => setPlaying(false));
    } else if (autoplay && !autoplayTriedRef.current) {
      // Autoplay: we're already muted, so this should succeed everywhere.
      autoplayTriedRef.current = true;
      const tryStart = async () => {
        const el = audioRef.current;
        if (!el) return;
        try {
          await el.play();
          setPlaying(true);
        } catch {
          // Extremely rare: even muted autoplay was blocked (e.g. Low-Data
          // mode). User will just tap the play button — no harm done.
        }
      };
      // If the element is still loading, wait for canplay; otherwise go now
      if (audioRef.current.readyState >= 2) {
        tryStart();
      } else {
        audioRef.current.addEventListener('canplay', tryStart, { once: true });
        // Safety timer — don't wait longer than 3s
        setTimeout(tryStart, 3000);
      }
    }
  }, [state, playing, volume, muted, autoplay]);

  // Auto-unmute on the FIRST user gesture anywhere on the page (common UX
  // pattern: a single click/tap/scroll anywhere confirms they want sound).
  useEffect(() => {
    if (!awaitingUnmute) return;
    const unmuteNow = () => {
      if (!audioRef.current) return;
      audioRef.current.muted = false;
      setMuted(false);
      setAwaitingUnmute(false);
    };
    window.addEventListener('pointerdown', unmuteNow, { once: true, capture: true });
    window.addEventListener('keydown', unmuteNow, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', unmuteNow, { capture: true });
      window.removeEventListener('keydown', unmuteNow, { capture: true });
    };
  }, [awaitingUnmute]);

  // Volume / mute
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  // ─── Media Session API (CarPlay / Android Auto / Lock Screen) ───────────
  // Broadcasts track title, artist, album ("One Voice Distro · {Station}"),
  // and artwork so the car screen / lock-screen shows meaningful metadata
  // instead of a generic "Independent Music" fallback.
  useEffect(() => {
    if (!('mediaSession' in navigator) || !displayTrack) return;

    const stationLabel = STATION_TITLES[station] || 'Radio';
    // Build an absolute https artwork URL. Accept either a filename stored
    // in `artwork` (then serve via /api/artwork) or an already-absolute URL
    // (e.g. R2 CDN link). Fall back to the One Voice Distro brand mark.
    const rawArt = displayTrack.artwork
      || displayTrack.cover_art
      || displayTrack.album_artwork;
    let artUrl = null;
    if (rawArt) {
      artUrl = rawArt.startsWith('http')
        ? rawArt
        : `${BACKEND_URL}/api/artwork/${rawArt}`;
    }
    // Supplying the same URL at multiple declared sizes lets CarPlay pick
    // whichever size it renders at — iOS picks the closest match.
    const artwork = artUrl
      ? [
          { src: artUrl, sizes: '96x96',   type: 'image/jpeg' },
          { src: artUrl, sizes: '192x192', type: 'image/jpeg' },
          { src: artUrl, sizes: '256x256', type: 'image/jpeg' },
          { src: artUrl, sizes: '384x384', type: 'image/jpeg' },
          { src: artUrl, sizes: '512x512', type: 'image/jpeg' },
        ]
      : [
          // Brand-mark fallback — always produces a One Voice Distro logo on
          // the car screen when a track has no artwork of its own.
          { src: `${BACKEND_URL}/nowplaying-logo.png`, sizes: '512x512', type: 'image/png' },
        ];

    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: displayTrack.title || 'One Voice Distro',
        artist: displayTrack.artist || displayTrack.username || 'One Voice Distro',
        album: `One Voice Distro · ${stationLabel}`,
        artwork,
      });
    } catch (e) {
      // Older browsers may not support the MediaMetadata constructor.
    }
  }, [displayTrack, station]);

  // Keep Media Session playback state in sync so CarPlay's play/pause icon
  // reflects reality.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch { /* noop */ }
  }, [playing]);

  // Wire CarPlay / Android Auto transport-control buttons. Radio is a
  // locked continuous stream — we expose play/pause only (next/prev are
  // not allowed so listeners can't skip past a featured track). Locked
  // streams reject pause silently with a toast matching the UI rules.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    const handlers = [
      ['play', () => {
        if (!audioRef.current) return;
        audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
      }],
      ['pause', () => {
        if (!audioRef.current) return;
        if (locked && !adminOverride) return; // no hard pause on locked radio
        audioRef.current.pause();
        setPlaying(false);
      }],
      ['stop', () => {
        if (!audioRef.current) return;
        audioRef.current.pause();
        setPlaying(false);
      }],
      // Explicitly null out prev/next/seek so the car UI hides those buttons
      ['previoustrack', null],
      ['nexttrack', null],
      ['seekbackward', null],
      ['seekforward', null],
      ['seekto', null],
    ];
    handlers.forEach(([action, handler]) => {
      try { ms.setActionHandler(action, handler); } catch { /* ignore */ }
    });
    return () => {
      handlers.forEach(([action]) => {
        try { ms.setActionHandler(action, null); } catch { /* ignore */ }
      });
    };
  }, [locked, adminOverride]);

  // Local position ticker (smoother UI than poll interval). 250ms gives a
  // crisp progress bar without burning CPU on idle tabs.
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      if (audioRef.current && !audioRef.current.paused) {
        setPos(audioRef.current.currentTime);
        // Accrue play-time for stream-record (counts while muted too — the
        // audio element IS playing, just silenced).
        playedSecondsRef.current += 0.25;
        if (!recordedRef.current && playedSecondsRef.current >= 60) {
          recordedRef.current = true;
          const track = state?.current_track;
          if (track?.id) {
            const ctype = track.is_ai_content || track.uploaded_by_ai_artist
              ? 'ai_track'
              : 'track';
            axios.post(
              `${API}/stream/record`,
              {
                content_type: ctype,
                content_id: track.id,
                duration_played: Math.floor(playedSecondsRef.current),
                source: `radio_${station}`,
              },
              token ? { headers: { Authorization: `Bearer ${token}` } } : {},
            ).catch(() => { /* silent — rate limits / network are fine */ });
          }
        }
      }
    }, 250);
    return () => clearInterval(id);
  }, [playing, state, station, token]);

  // Keep personal-purchases queue in sync whenever state arrives
  useEffect(() => {
    if (personalize && state?.personal_purchases?.length) {
      personalQueueRef.current = state.personal_purchases;
    }
  }, [personalize, state]);

  // Play a standalone track (used for VIP requests + personal purchases).
  // Bypasses the global schedule; returns a Promise that resolves when
  // the audio starts playing (or rejects on error).
  const playStandaloneTrack = useCallback((track, slateText) => {
    if (!track?.filename) return Promise.reject(new Error('no filename'));
    if (!audioRef.current) audioRef.current = new Audio();
    lastTrackIdRef.current = track.id;

    if (slateText) {
      setActiveSlate(slateText);
      setTimeout(() => setActiveSlate(null), 8000);
    }
    audioRef.current.src = `${BACKEND_URL}/api/stream/track/${track.filename}`;
    audioRef.current.currentTime = 0;
    // Broadcast the standalone (VIP / personal) track to CarPlay so the
    // listener sees who's being shouted out / what's overlayed.
    setDisplayTrack(track);
    return audioRef.current.play().catch(() => setPlaying(false));
  }, []);

  // Play the TTS slate first, then the VIP track seamlessly.
  const playVipWithSlate = useCallback(async (track, username, slateText) => {
    const slateAudio = new Audio(
      `${API}/radio/vip-slate/${encodeURIComponent(username || 'anonymous')}`
    );
    slateAudio.volume = audioRef.current ? audioRef.current.volume : volume;
    setActiveSlate(slateText || `Requested by @${username} · VIP Member`);

    return new Promise((resolve) => {
      let settled = false;
      const proceed = async () => {
        if (settled) return;
        settled = true;
        setTimeout(() => setActiveSlate(null), 6000);
        await playStandaloneTrack(track, null);
        resolve();
      };
      slateAudio.onended = proceed;
      slateAudio.onerror = proceed;
      // Safety timeout — don't wait more than 7s for the slate
      setTimeout(proceed, 7000);
      slateAudio.play().catch(proceed);
    });
  }, [volume, playStandaloneTrack]);
  // When a track ends, try to claim the next VIP request (if any),
  // otherwise resync from the schedule.
  const handleTrackEnd = useCallback(async () => {
    slotCounterRef.current += 1;

    try {
      // 1. VIP claim first (peak hours, queued request)
      const current = state || await fetchNow();
      if (current?.pending_vip_request) {
        const claim = await axios.post(
          `${API}/radio/claim-next-vip-request?station=${station}`,
          {},
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (claim.data?.claimed && claim.data?.track) {
          const vipTrack = claim.data.track;
          const requester = claim.data.request?.username;
          await playVipWithSlate(
            vipTrack,
            requester,
            claim.data.intro_slate,
          );
          return;
        }
      }

      // 2. Personal purchase overlay — every Nth slot when personalize=true
      const every = current?.personal_interleave_every || 4;
      if (
        personalize
        && personalQueueRef.current.length > 0
        && slotCounterRef.current % every === 0
      ) {
        // Rotate through the user's purchased pool
        const idx = Math.floor(slotCounterRef.current / every) % personalQueueRef.current.length;
        const pTrack = personalQueueRef.current[idx];
        if (pTrack?.filename) {
          await playStandaloneTrack(
            pTrack,
            `From your library · ${pTrack.title}`,
          );
          return;
        }
      }
    } catch (e) {
      console.warn('[radio] claim next failed:', e);
    }

    // 3. Fallback: resync from the global schedule
    const fresh = await fetchNow();
    if (fresh?.current_track && audioRef.current) {
      // Force a track-change so the src-update effect reloads
      if (fresh.current_track.id === lastTrackIdRef.current) {
        audioRef.current.currentTime = fresh.seek_seconds || 0;
        audioRef.current.play().catch(() => setPlaying(false));
      } else {
        lastTrackIdRef.current = null;
        setState({ ...fresh });
      }
    }
  }, [state, station, token, fetchNow, personalize, playVipWithSlate, playStandaloneTrack]);

  // Wire onended once
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.onended = handleTrackEnd;
    return () => {
      if (audioRef.current) audioRef.current.onended = null;
    };
  }, [handleTrackEnd]);

  const handleTogglePlay = async () => {
    if (!audioRef.current) return;
    // In locked mode (All-Genre radio), non-admin users CANNOT pause once
    // playback has started. They can only mute. The play button is shown
    // on initial load to satisfy browser autoplay requirements.
    if (playing && locked && !adminOverride) {
      toast.info('This is a live continuous stream — tap the speaker icon to mute.');
      return;
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      // On resume, first re-sync to server position (nobody likes stale seek)
      const fresh = await fetchNow();
      if (fresh?.seek_seconds != null) {
        audioRef.current.currentTime = fresh.seek_seconds;
      }
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch (e) {
        toast.error('Playback blocked — click play again');
        setPlaying(false);
      }
    }
  };
  // ─── VIP / non-VIP advanced controls ────────────────────────────────
  // These are LOCAL seeks — they only affect this listener's audio
  // tag, not other listeners on the same station. The radio stream
  // stays in server-clock sync; we simply scrub the local <audio> tag.

  const handleSeekRelative = (deltaSec) => {
    if (!audioRef.current) return;
    if (!advancedControlsEnabled) {
      toast.info('Live continuous stream — seeking is locked.');
      return;
    }
    // Streamed audio sometimes reports duration as Infinity. We still want
    // FF/RW to work: clamp to ≥0 and just attempt the seek. If the server
    // doesn't support range requests on this asset the browser silently
    // ignores the write — but for our normal MP3 stream it works.
    const cur = audioRef.current.currentTime || 0;
    const dur = audioRef.current.duration;
    const target = Number.isFinite(dur)
      ? Math.max(0, Math.min(dur - 0.5, cur + deltaSec))
      : Math.max(0, cur + deltaSec);
    audioRef.current.currentTime = target;
    // Reflect immediately in the UI — the 500ms ticker will sync up next
    setPos(target);
  };

  const handleSkipNext = async () => {
    if (!advancedControlsEnabled) {
      toast.info('Live continuous stream — skip is locked.');
      return;
    }
    // Non-VIP listeners get a quota of 3 skips / 15min on unlocked stations.
    if (!isVip) {
      const ok = skipQuota.skipsRemaining > 0 && !skipQuota.lockedUntil;
      if (!ok) {
        const remaining = formatCooldown(skipQuota.timeRemaining);
        toast.error(
          `Skip cooldown · ${remaining} remaining. Upgrade to VIP for unlimited skips.`
        );
        return;
      }
      // Server-side enforcement (defense in depth — prevents quota bypass
      // by clearing localStorage). Falls through on network errors so a
      // flaky connection never blocks a legitimate skip.
      if (token) {
        try {
          await axios.post(
            `${API}/radio/skip`,
            { station },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
          );
        } catch (err) {
          if (err?.response?.status === 429) {
            const detail = err.response.data?.detail || {};
            toast.error(
              detail.message || 'Skip limit reached. Upgrade to VIP for unlimited skips.'
            );
            return;
          }
          // network error → degrade gracefully, still allow client-side skip
        }
      }
      skipQuota.useSkip();
    }
    // Skip to the actual NEXT scheduled track (not just end-of-current).
    // Fetching /upcoming gives us the schedule one slot ahead; we play it
    // as a standalone overlay so we don't disrupt other listeners on the
    // same global stream.
    try {
      const r = await axios.get(
        `${API}/radio/upcoming?station=${station}&limit=1`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined, timeout: 8000 }
      );
      const nextT = r.data?.tracks?.[0];
      if (nextT?.filename) {
        playStandaloneTrack(nextT, `Skipped · ${nextT.title}`);
        return;
      }
    } catch { /* fall through */ }
    // Fallback: end the current track so onended fires + handleTrackEnd runs.
    const audio = audioRef.current;
    if (audio && Number.isFinite(audio.duration)) {
      audio.currentTime = Math.max(0, audio.duration - 0.5);
    } else {
      handleTrackEnd();
    }
  };

  const handlePreviousTrack = async () => {
    if (!advancedControlsEnabled || !isVip) {
      toast.info(isVip
        ? 'Live continuous stream — previous track is locked.'
        : 'Previous track is a VIP feature. Upgrade to unlock.');
      return;
    }
    try {
      const r = await axios.get(`${API}/radio/previous-track?station=${station}&offset=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const t = r.data?.track;
      if (t) {
        // Restart the previous track from the top by treating it as a
        // standalone overlay (won't disrupt other listeners).
        playStandaloneTrack(t);
      }
    } catch (e) {
      toast.error('Could not load previous track.');
    }
  };

  const fetchUpcoming = useCallback(async () => {
    if (!isVip || !advancedControlsEnabled) return;
    setUpcomingLoading(true);
    try {
      const r = await axios.get(`${API}/radio/upcoming?station=${station}&limit=5`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setUpcoming(r.data?.tracks || []);
    } catch {
      setUpcoming([]);
    } finally {
      setUpcomingLoading(false);
    }
  }, [station, token, isVip, advancedControlsEnabled]);

  const handleShare = async () => {
    // Copy the OG-preview URL for this station → paste into iMessage/WA/etc
    // always renders a rich card and tuning in instantly (auto-redirects to
    // the radio page).
    const ogUrl = `${BACKEND_URL}/api/og/radio/${station}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: STATION_TITLES[station] || 'One Voice Radio',
          text: `Tune into ${STATION_TITLES[station] || 'One Voice Radio'} — live 24/7 on One Voice Distro`,
          url: ogUrl,
        });
      } else {
        await navigator.clipboard.writeText(ogUrl);
        toast.success('Radio link copied!');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(ogUrl);
          toast.success('Radio link copied!');
        } catch {
          toast.error('Copy failed');
        }
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const track = state?.current_track;
  const next = state?.next_track;
  const audioDur = audioRef.current?.duration;
  const effectiveDuration = (Number.isFinite(audioDur) && audioDur > 0)
    ? audioDur
    : (track?.duration || 0);
  const progressPct = effectiveDuration ? Math.min(100, (pos / effectiveDuration) * 100) : 0;

  // Compute the contextual "Buy this track / beat / AI track" CTA once,
  // so both the inline player and the fullscreen overlay can show it.
  // Only show for PAID content (free / promo tracks hide the CTA).
  const buyCta = (() => {
    if (!track?.id) return null;
    const isPaid = !track.is_free && (Number(track.price) > 0 || Number(track.lease_price) > 0);
    if (!isPaid) return null;
    const isBeat = !!(track.is_beat || station === 'beats');
    const isAi = !!(track.is_ai_content || track.uploaded_by_ai_artist || station === 'ai');
    const slugOrId = track.slug || track.id;
    return {
      href: isBeat ? `/beat/${slugOrId}` : `/track/${slugOrId}`,
      label: isBeat ? 'Buy this beat' : isAi ? 'Buy this AI track' : 'Buy this track',
    };
  })();

  // Per-station palette — block party / car-trunk-speaker vibe. Each
  // station gets its own primary accent + glow so the player FEELS
  // different across stations even though the chrome is shared.
  const theme = ({
    all:   { primary: '#FFDA33', secondary: '#FF6B1A', glow: 'rgba(255,107,26,0.5)', accent: 'gold', name: 'All Genre' },
    music: { primary: '#00CCFF', secondary: '#0066FF', glow: 'rgba(0,204,255,0.5)',  accent: 'cyan', name: 'Music' },
    ai:    { primary: '#FF66AA', secondary: '#9333EA', glow: 'rgba(255,102,170,0.5)', accent: 'pink', name: 'AI' },
    beats: { primary: '#00ff88', secondary: '#00b386', glow: 'rgba(0,255,136,0.5)',  accent: 'green', name: 'Beats' },
  })[station] || { primary: '#FFDA33', secondary: '#FF6B1A', glow: 'rgba(255,107,26,0.5)', accent: 'gold', name: 'Radio' };

  return (
    <div
      className="relative rounded-2xl overflow-hidden border-2 preserve-dark group"
      data-preserve-dark="true"
      data-testid={`synced-radio-${station}`}
      style={{
        borderColor: `${theme.primary}40`,
        background: `radial-gradient(circle at 0% 0%, ${theme.primary}22 0%, transparent 35%), radial-gradient(circle at 100% 100%, ${theme.secondary}22 0%, transparent 40%), linear-gradient(135deg, #000 0%, #0a0a0a 50%, #050505 100%)`,
        boxShadow: `0 0 40px ${theme.glow}, inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}
    >
      {/* Speaker grill texture overlay — gives the "trunk speaker" tactile feel */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1.5px)',
          backgroundSize: '8px 8px',
        }}
      />

      {/* OneVoice Distro circular spinning logo — top-RIGHT brand stamp.
          (Was top-left but overlapped the track artwork on mobile.)
          Slow continuous spin while audio is playing, paused otherwise.
          Glows with the active station's accent for a "vinyl turntable" feel. */}
      <div
        className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 pointer-events-none select-none"
        data-testid="radio-brand-logo"
        aria-hidden="true"
      >
        <div
          className="relative rounded-full overflow-hidden border-2"
          style={{
            width: 36,
            height: 36,
            borderColor: `${theme.primary}AA`,
            boxShadow: playing
              ? `0 0 18px ${theme.primary}AA, 0 0 6px ${theme.primary}80, inset 0 0 6px rgba(0,0,0,0.6)`
              : `0 0 8px ${theme.primary}55, inset 0 0 6px rgba(0,0,0,0.6)`,
            background: '#000',
          }}
        >
          <img
            src="/onevoice-logo-circular.png"
            alt="One Voice Distro"
            className="w-full h-full object-cover"
            style={{
              animation: playing ? 'ovd-spin 4.5s linear infinite' : 'none',
              willChange: 'transform',
            }}
          />
          {/* Center "spindle" dot for the vinyl/turntable feel */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              width: 7,
              height: 7,
              background: '#000',
              boxShadow: `0 0 0 1.5px ${theme.primary}, inset 0 0 2px rgba(255,255,255,0.6)`,
            }}
          />
        </div>
        <style>{`@keyframes ovd-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
      {/* Pulsing bass-glow ring when playing — block-party hype */}
      {playing && (
        <>
          <div
            className="absolute -inset-1 rounded-2xl opacity-50 pointer-events-none animate-pulse"
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, transparent 60%, ${theme.secondary})`,
              filter: 'blur(20px)',
              animationDuration: '2.4s',
            }}
          />
          <div
            className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse z-10"
            style={{ background: theme.primary, boxShadow: `0 0 12px ${theme.primary}` }}
          />
        </>
      )}
      {/* VIP Intro Slate banner (appears briefly when a VIP request plays) */}
      {activeSlate && (
        <div
          className="bg-gradient-to-r from-[#FFDA33]/90 to-[#FFE97A]/90 text-black px-4 py-2 font-['Chakra_Petch'] uppercase tracking-wider text-sm flex items-center gap-2"
          data-testid="radio-vip-intro-slate"
        >
          <Sparkles size={14} />
          {activeSlate}
        </div>
      )}

      {/* Autoplay-muted CTA: shown when the browser blocks audible autoplay
          and we start muted. The banner itself is clickable to unmute. */}
      {awaitingUnmute && (
        <button
          type="button"
          onClick={() => {
            if (audioRef.current) audioRef.current.muted = false;
            setMuted(false);
            setAwaitingUnmute(false);
          }}
          className="w-full bg-gradient-to-r from-[#FFDA33] to-[#FFE97A] text-black px-4 py-2 flex items-center justify-center gap-2 font-mono uppercase tracking-widest text-xs hover:brightness-110"
          data-testid="radio-tap-to-unmute"
        >
          <VolumeX size={14} /> Live — tap to unmute
        </button>
      )}

      <div className="relative p-5 sm:p-6 pt-7 sm:pt-7 flex flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-5">
        {/* Artwork — squared with chunky border + active scale animation. */}
        <div className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0">
          {track?.artwork ? (
            <img
              src={track.artwork.startsWith('http') ? track.artwork : `${BACKEND_URL}/api/artwork/${track.artwork}`}
              alt=""
              className={`w-full h-full object-cover rounded-lg transition-transform duration-500 ${playing ? 'scale-100' : 'scale-95'}`}
              style={{ boxShadow: playing ? `0 0 24px ${theme.glow}` : 'none' }}
            />
          ) : (
            <div
              className="w-full h-full rounded-lg flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${theme.primary}22, #111)` }}
            >
              <Radio size={36} style={{ color: theme.primary }} />
            </div>
          )}
          {playing && (
            <>
              <div
                className="absolute inset-0 rounded-lg border-2 animate-pulse pointer-events-none"
                style={{ borderColor: theme.primary, animationDuration: '1.6s' }}
              />
              {/* Sub-woofer pulse rings */}
              <div
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{ boxShadow: `0 0 0 4px ${theme.primary}30, 0 0 30px ${theme.glow}` }}
              />
            </>
          )}
        </div>

        {/* Info + progress */}
        <div className="order-3 sm:order-none w-full sm:flex-1 min-w-0 sm:min-w-[160px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {STATION_TITLES[station] && (
              <span className="whitespace-nowrap shrink-0 text-[11px] sm:text-[12px] font-['Chakra_Petch'] uppercase tracking-[0.18em] text-white px-2 py-0.5 rounded-full border border-white/15 bg-white/5" data-testid="radio-station-title">
                {STATION_TITLES[station]}
              </span>
            )}
            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-[#FFDA33]/15 border border-[#FFDA33]/40 rounded text-[9px] font-mono uppercase text-[#FFDA33]">
              <Users size={9} /> LIVE · Everyone's listening
            </div>
            {state?.peak_hours && (
              <span className="text-[9px] font-mono uppercase text-[#00ff88] bg-[#00ff88]/15 px-1.5 py-0.5 rounded">
                PEAK · VIP REQUESTS ON
              </span>
            )}
            {state?.pending_vip_request && (
              <span className="text-[9px] font-mono uppercase text-[#FFDA33] bg-[#FFDA33]/15 px-1.5 py-0.5 rounded">
                🎙 VIP request queued
              </span>
            )}
          </div>
          <MarqueeText
            text={track?.title || 'Tune in…'}
            speedPxPerSec={45}
            className="font-['Chakra_Petch'] font-extrabold uppercase tracking-wide text-2xl sm:text-3xl leading-tight"
            data-testid="radio-current-title"
            style={{
              color: '#ffffff',
              textShadow: `0 0 18px ${theme.primary}, 0 0 32px ${theme.glow}, 0 2px 4px rgba(0,0,0,0.85)`,
              WebkitTextStroke: `0.4px ${theme.primary}55`,
              letterSpacing: '0.02em',
            }}
          />
          <MarqueeText
            text={track?.artist || '—'}
            speedPxPerSec={35}
            className="font-['Chakra_Petch'] font-bold text-base sm:text-lg mt-0.5"
            style={{
              color: theme.primary,
              textShadow: `0 0 10px ${theme.glow}, 0 1px 3px rgba(0,0,0,0.85)`,
            }}
          />

          {/* "Now playing from album" ribbon — surfaces the parent album so
              listeners can jump to the full release. Only renders when the
              current radio track is linked to an album. */}
          {(track?.album_title || track?.album_id) && (
            <Link
              to={`/album/${track.album_id || track.album_title}`}
              className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-[#FFDA33]/40 bg-[#FFDA33]/10 hover:bg-[#FFDA33]/20 hover:border-[#FFDA33] transition-colors group/album max-w-full"
              data-testid="radio-now-playing-from-album"
              title={`Open album · ${track.album_title || ''}`}
            >
              <Disc size={11} className="text-[#FFDA33] flex-shrink-0" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#FFDA33]/80 flex-shrink-0">
                From album
              </span>
              <span aria-hidden className="text-[#FFDA33]/60 font-mono text-[10px] flex-shrink-0">·</span>
              <span className="font-['Chakra_Petch'] text-[12px] uppercase tracking-wide text-white truncate group-hover/album:text-[#FFDA33]">
                {track.album_title || 'View album'}
              </span>
              <ArrowRight size={11} className="text-[#FFDA33] flex-shrink-0 opacity-0 group-hover/album:opacity-100 transition-opacity" />
            </Link>
          )}

          {/* Animated waveform — visualises live audio energy. Reactive
              when Web Audio AnalyserNode can attach to the stream (CORS-safe);
              otherwise gracefully degrades to the CSS-driven equalizer. */}
          <div className="mt-3" data-testid="radio-waveform-wrap">
            <AnimatedWaveform
              playing={playing && !muted}
              accent={STATION_ACCENT[station] || 'gold'}
              height={40}
              testid="radio-waveform"
              levels={analyser.ready ? analyser.levels : null}
            />
          </div>

          {/* Banner ad — only renders for non-VIP / non-PTL listeners.
              The ad engine returns is_ad_free for VIPs / active PTL
              members so this slot stays clean for them. */}
          <div className="-mx-5">
            <RadioBannerAd station={station} token={token} theme={theme} />
          </div>

          {/* Progress — taller, full-width, prominent time labels in m:ss */}
          <div className="mt-3 w-full">
            <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden border border-[#222] shadow-inner">
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-linear"
                style={{
                  width: `${progressPct}%`,
                  background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary || theme.primary})`,
                  boxShadow: `0 0 12px ${theme.glow}`,
                }}
                data-testid="radio-progress-fill"
              />
            </div>
            <div className="flex justify-between items-center mt-1.5 font-mono tabular-nums">
              <span className="text-sm font-bold text-white" data-testid="radio-current-time">
                {fmt(pos)}
              </span>
              <span className="text-sm font-medium text-[#aaa]" data-testid="radio-total-time">
                {fmt(effectiveDuration)}
              </span>
            </div>
          </div>

          {/* Buy this beat / track / AI track CTA — only renders if the
              track is paid content. Free / promo tracks hide the CTA so
              the radio doesn't push a purchase that doesn't exist. */}
          {track?.id && !track?.is_free && (Number(track?.price) > 0 || Number(track?.lease_price) > 0) && (() => {
            const isBeat = !!(track.is_beat || station === 'beats');
            const isAi = !!(track.is_ai_content || track.uploaded_by_ai_artist || station === 'ai');
            const slugOrId = track.slug || track.id;
            const ctaHref = isBeat
              ? `/beat/${slugOrId}`
              : `/track/${slugOrId}`;
            const ctaLabel = isBeat
              ? 'Buy this beat'
              : isAi
                ? 'Buy this AI track'
                : 'Buy this track';
            return (
              <Link
                to={ctaHref}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#00ff88] hover:text-white hover:bg-[#00ff88]/10 px-2 py-1 rounded border border-[#00ff88]/30 transition-colors"
                data-testid="radio-buy-cta"
              >
                <ShoppingBag size={11} /> {ctaLabel} <ArrowRight size={11} />
              </Link>
            );
          })()}

          {/* Next up */}
          {next && (
            <p className="text-[#666] font-mono text-[10px] mt-1 truncate">
              Up next: <span className="text-[#aaa]">{next.title} — {next.artist}</span>
            </p>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center gap-2 shrink-0 ml-auto sm:ml-0">
          <div className="relative">
            {/* Soft halo pulse when playing — tells the eye the player is alive */}
            {playing && (
              <span
                aria-hidden
                className="absolute inset-0 rounded-full bg-[#FFDA33]/40 animate-ping pointer-events-none"
                style={{ animationDuration: '2.2s' }}
              />
            )}
            <Button
              onClick={handleTogglePlay}
              disabled={playing && locked && !adminOverride}
              className={
                "relative w-14 h-14 rounded-full bg-[#FFDA33] text-black transition-all duration-200 ease-out shadow-[0_0_24px_rgba(255,218,51,0.35)] "
                + ((playing && locked && !adminOverride)
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:bg-[#FFE97A] hover:scale-110 hover:shadow-[0_0_32px_rgba(255,218,51,0.6)] active:scale-95")
              }
              data-testid="radio-toggle-play"
              title={playing && locked && !adminOverride ? "This is a live continuous stream — use the speaker icon to mute." : ""}
            >
              {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                setMuted(m => !m);
                setAwaitingUnmute(false);
              }}
              className="text-[#888] hover:text-white p-1.5 rounded-full transition-all duration-150 hover:bg-white/5 hover:scale-110 active:scale-95"
              data-testid="radio-toggle-mute"
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
              className="w-16 accent-[#FFDA33]"
              data-testid="radio-volume-slider"
            />
            {shareable && (
              <button
                onClick={handleShare}
                className="ml-1 text-[#888] hover:text-[#FFDA33] p-1.5 rounded-full transition-all duration-150 hover:bg-[#FFDA33]/10 hover:scale-110 active:scale-95"
                data-testid="radio-share-btn"
                title="Share this radio"
              >
                <Share2 size={14} />
              </button>
            )}
            <button
              onClick={() => setFullscreen(true)}
              className="ml-1 p-2.5 sm:p-1.5 rounded-full transition-all duration-150 hover:bg-white/5 hover:scale-110 active:scale-95"
              style={{ color: theme.primary }}
              data-testid="radio-fullscreen-btn"
              title="Open Now Playing fullscreen"
              aria-label="Open Now Playing fullscreen"
            >
              <Maximize2 size={18} className="sm:hidden" />
              <Maximize2 size={14} className="hidden sm:block" />
            </button>
          </div>

          {/* Advanced controls — VIP gets full set; non-VIP gets the
              "Skip Next" button with a quota counter; locked stations
              (All-Genre) hide the row entirely. */}
          {advancedControlsEnabled && (
            <div className="flex items-center gap-1 mt-1 flex-wrap justify-center" data-testid="radio-advanced-controls">
              <button
                onClick={handlePreviousTrack}
                className={`p-1.5 rounded-full transition-all duration-150 ${
                  isVip ? 'hover:bg-white/5 hover:scale-110 active:scale-95' : 'opacity-40 cursor-not-allowed'
                }`}
                style={{ color: isVip ? theme.primary : '#666' }}
                data-testid="radio-prev-track-btn"
                title={isVip ? 'Previous track' : 'VIP only — previous track'}
                aria-label="Previous track"
                disabled={!isVip}
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => handleSeekRelative(-15)}
                className={`p-1.5 rounded-full transition-all duration-150 ${
                  isVip ? 'hover:bg-white/5 hover:scale-110 active:scale-95' : 'opacity-40 cursor-not-allowed'
                }`}
                style={{ color: isVip ? '#fff' : '#666' }}
                data-testid="radio-rewind-btn"
                title={isVip ? 'Rewind 15s' : 'VIP only — rewind'}
                aria-label="Rewind 15 seconds"
                disabled={!isVip}
              >
                <Rewind size={14} />
              </button>
              <button
                onClick={() => handleSeekRelative(15)}
                className={`p-1.5 rounded-full transition-all duration-150 ${
                  isVip ? 'hover:bg-white/5 hover:scale-110 active:scale-95' : 'opacity-40 cursor-not-allowed'
                }`}
                style={{ color: isVip ? '#fff' : '#666' }}
                data-testid="radio-forward-btn"
                title={isVip ? 'Forward 15s' : 'VIP only — fast-forward'}
                aria-label="Fast-forward 15 seconds"
                disabled={!isVip}
              >
                <FastForward size={14} />
              </button>
              <button
                onClick={handleSkipNext}
                className={`relative px-2 py-1.5 rounded-full transition-all duration-150 hover:scale-110 active:scale-95 flex items-center gap-1 ${
                  (!isVip && (skipQuota.skipsRemaining === 0 || skipQuota.lockedUntil))
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-white/5'
                }`}
                style={{ color: theme.primary }}
                data-testid="radio-skip-next-btn"
                title={
                  isVip
                    ? 'Skip to next track'
                    : skipQuota.lockedUntil
                      ? `Skip cooldown · ${formatCooldown(skipQuota.timeRemaining)} left`
                      : `${skipQuota.skipsRemaining}/${SKIP_QUOTA} skips remaining (15-min reset)`
                }
                aria-label="Skip to next track"
                disabled={!isVip && (skipQuota.skipsRemaining === 0 || !!skipQuota.lockedUntil)}
              >
                <SkipForward size={14} />
                {!isVip && (
                  <span className="font-mono text-[9px] tabular-nums" style={{ color: theme.primary }}>
                    {skipQuota.lockedUntil
                      ? formatCooldown(skipQuota.timeRemaining)
                      : `${skipQuota.skipsRemaining}/${SKIP_QUOTA}`}
                  </span>
                )}
              </button>
              {isVip && (
                <button
                  onClick={() => {
                    const next = !showQueue;
                    setShowQueue(next);
                    if (next) fetchUpcoming();
                  }}
                  className="p-1.5 rounded-full transition-all duration-150 hover:bg-white/5 hover:scale-110 active:scale-95"
                  style={{ color: showQueue ? theme.primary : '#fff' }}
                  data-testid="radio-queue-btn"
                  title="Up next queue (VIP)"
                  aria-label="Show upcoming queue"
                >
                  <ListMusic size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* VIP "Up Next" queue — fetched on demand from /api/radio/upcoming.
          Listening stays in server-clock sync; this is purely informative. */}
      {advancedControlsEnabled && isVip && showQueue && (
        <div
          ref={queuePanelRef}
          className="mx-5 mb-4 rounded-xl border border-[#1a1a1a] bg-[#0a0a0a] p-4 max-h-[60vh] overflow-y-auto"
          data-testid="radio-queue-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <ListMusic size={14} style={{ color: theme.primary }} />
            <span
              className="font-mono uppercase tracking-widest text-[10px]"
              style={{ color: theme.primary }}
            >
              Up Next · VIP
            </span>
          </div>
          {upcomingLoading ? (
            <div className="text-[#666] font-mono text-xs">Loading queue…</div>
          ) : upcoming.length === 0 ? (
            <div className="text-[#666] font-mono text-xs">Queue not available.</div>
          ) : (
            <ol className="space-y-1.5">
              {upcoming.map((t, i) => (
                <li
                  key={`${t.id || t.audio_url}-${i}`}
                  className="flex items-center justify-between gap-3 text-xs font-mono"
                >
                  <span className="text-[#666] w-5 tabular-nums">{i + 1}.</span>
                  <span className="flex-1 min-w-0 text-white truncate">{t.title || 'Untitled'}</span>
                  <span className="text-[#888] truncate max-w-[40%]">{t.artist || '—'}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Locked-stream notice (All-Genre 24/7) */}
      {locked && !adminOverride && (
        <div className="px-5 pb-2 text-[10px] font-mono text-[#666] flex items-center gap-1.5">
          <Radio size={10} className="text-[#FFDA33]" />
          Live 24/7 continuous stream · controls limited to mute / volume to keep every listener in sync
        </div>
      )}

      {/* Personal purchases badge */}
      {personalize && state?.personal_purchases?.length > 0 && (
        <div className="px-5 pb-3 text-[10px] font-mono text-[#888]">
          <Sparkles size={10} className="inline mr-1 text-[#FFDA33]" />
          Your radio includes <span className="text-[#00ff88] font-bold">{state.personal_purchases.length}</span>
          {' '}of your purchased tracks (played every {state.personal_interleave_every || 4} slots).
        </div>
      )}

      {/* Fullscreen "Now Playing" overlay (CarPlay-style) */}
      <RadioFullscreenOverlay
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        track={track}
        next={next}
        pos={pos}
        progressPct={progressPct}
        playing={playing}
        muted={muted}
        volume={volume}
        awaitingUnmute={awaitingUnmute}
        station={station}
        stations={stations || []}
        onStationChange={onStationChange}
        theme={theme}
        locked={locked}
        adminOverride={adminOverride}
        shareable={shareable}
        onTogglePlay={handleTogglePlay}
        onToggleMute={() => { setMuted((m) => !m); setAwaitingUnmute(false); }}
        onUnmute={() => {
          if (audioRef.current) audioRef.current.muted = false;
          setMuted(false);
          setAwaitingUnmute(false);
        }}
        onVolumeChange={(v) => { setVolume(v); setMuted(false); }}
        onShare={handleShare}
        buyHref={buyCta?.href}
        buyLabel={buyCta?.label}
      />
    </div>
  );
}

export default SyncedRadioPlayer;
